import { useNavigate, useSearch } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { FeatureErrorBoundary } from '@nucleus-commerce/admin-kit/errors';
import { PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { Card } from '@nucleus-commerce/ui/components/card';
import { Label } from '@nucleus-commerce/ui/components/label';
import { Switch } from '@nucleus-commerce/ui/components/switch';

import { canManageOrganizations } from '@/shared/session/roles';
import { session } from '@/shared/session/session';

import { SupportSessionsTable } from '../components/support-sessions-table';
import { useOrganizationOptions } from '../hooks/use-organization-options';

const ROUTE_ID = '/_authed/support-sessions' as const;

function SupportSessions() {
  const { t } = useTranslation('organizations');
  const { me } = session.useSession();
  const search = useSearch({ from: ROUTE_ID });
  const navigate = useNavigate({ from: '/support-sessions' });
  const options = useOrganizationOptions();
  const names = useMemo(
    () => new Map((options.data ?? []).map((option) => [option.id, option.name])),
    [options.data],
  );
  const showAll = search.all === true;

  return (
    <PageContent>
      <PageHeader
        title={
          <span className="font-serif text-[1.875rem] font-normal">{t('sessions.pageTitle')}</span>
        }
        description={t('sessions.pageDescription')}
        actions={
          <span
            className="flex items-center gap-2"
            data-tour="platform.support-sessions.show-ended"
          >
            <Switch
              id="support-sessions-all"
              checked={showAll}
              onCheckedChange={(checked) =>
                void navigate({ search: checked ? { all: true } : {}, replace: true })
              }
            />
            <Label htmlFor="support-sessions-all">{t('sessions.showEnded')}</Label>
          </span>
        }
      />
      <Card className="gap-0 overflow-hidden py-0" data-tour="platform.support-sessions.table">
        <SupportSessionsTable
          filter={showAll ? {} : { active: true }}
          canManage={canManageOrganizations(me?.user.role)}
          organizationNames={names}
          label={t('sessions.pageTitle')}
        />
      </Card>
    </PageContent>
  );
}

export function SupportSessionsPage() {
  return (
    <FeatureErrorBoundary name="organizations.support-sessions">
      <SupportSessions />
    </FeatureErrorBoundary>
  );
}
